import { useContext, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Title, Paper, Text, Divider, TextSearch, Tabs, Group, Button } from 'components'
import { useMantineTheme, useMediaQuery, rem, useFeatureToggle } from 'hooks'
import { useCurrentUser } from 'context'
import { ProjectStepsButtons } from '../ProjectStepsButtons'
import { IconBooks, IconMoodCog, IconSparkles } from '@tabler/icons-react'
import { ListSkillsStandardsPage1 } from '../ListSkillsStandardsPage1'
import { SkillsStandardsContext } from '../../context/SkillsStandardsContext'
import { ListSkillsStandardsPage2 } from '../ListSkillsStandardsPage2'
import { ReviewSkillsStandardsModal } from '../ReviewSkillsStandardsModal'
import { ListSearchResults } from '../ListSearchResults'
import { MoreInfoSkillsStandardsModal } from '../MoreInfoSkillsStandardsModal'
import { WorkedProjectsSkillsStandardsModal } from '../WorkedProjectsSkillsStandardsModal'
import { ChangedDataConfirmModal } from 'components'
import { StepperContext } from '../../context/StepperContext'
import { AISuggestionsModalV2 } from '../AISuggestionsModal/AISuggestionsModalV2'
import { useAIGeneratorContext } from '../../hooks/useAIGeneratorContext'
import { AIGeneratorContext } from '../AIGeneratorModal/interfaces'

export function ProjectSkillsStandards() {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const user = useCurrentUser()
  const { id: projectId } = useParams<{ id: string }>()
  const askAIEnabled = useFeatureToggle('ask_ai')
  const standardsOnly = user.currentUser?.selected_school?.enable_only_bncc_content
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const {
    skillsStandards,
    targetRef,
    page,
    currentTab,
    setCurrentTab,
    handleChangeTab,
    handleChangeSearch,
    searchTerm,
    searchResults,
    selectedSkills,
    selectedStandards,
    handleDiscard,
    selectedChanged,
  } = useContext(SkillsStandardsContext)

  const { confirmModalOpened, confirmModalClose } = useContext(StepperContext)
  const [aiSuggestionsModalOpened, setAiSuggestionsModalOpened] = useState(false)

  // Usar hook que acessa localStorage diretamente
  // Para novos projetos, usar 'new', para edição usar o projectId
  const contextId = (!projectId || window.location.pathname.includes('/projects/new')) ? 'new' : projectId
  const { context: aiContext } = useAIGeneratorContext(contextId)

  // Estado local para armazenar dados da IA processados
  const [aiData, setAiData] = useState<AIGeneratorContext | null>(null)

  // Debug: verificar todos os contextos
  useEffect(() => {
    // Determinar se estamos criando ou editando
    const isNewProject = !projectId || window.location.pathname.includes('/projects/new')
    const storageKey = isNewProject ? 'ai_generator_context_new' : `ai_generator_context_${projectId}`

    // Verificar localStorage diretamente
    const storageData = localStorage.getItem(storageKey)

    if (storageData) {
      try {
        const parsed = JSON.parse(storageData)
        setAiData(parsed)
      } catch (e) {
        console.error('Erro ao fazer parse do localStorage:', e)
      }
    }
  }, [projectId, aiContext])

  // Verificar se há sugestões da IA
  const hasAISuggestions = useMemo(() => {
    try {
      const data = aiData || aiContext

      if (!data) {
        return false
      }

      // Usar sempre fullApiResponse.projectItems (camelCase)
      const items = data.fullApiResponse?.projectItems

      if (!items) {
        return false
      }

      const hasContents = Array.isArray(items.contents) && items.contents.length > 0
      const hasSkills = Array.isArray(items.specificSkills) && items.specificSkills.length > 0

      const result = hasContents || hasSkills
      
      return result
    } catch (error) {
      return false
    }
  }, [aiData, aiContext])

  const checkMutations = () => {
    return selectedChanged
  }

  useEffect(() => {
    if (standardsOnly) {
      setCurrentTab('content')
    } else {
      setCurrentTab('skill')
    }
  }, [])

  return (
    <>
      <ChangedDataConfirmModal discard={handleDiscard} opened={confirmModalOpened} close={confirmModalClose} />
      <MoreInfoSkillsStandardsModal />
      <WorkedProjectsSkillsStandardsModal />
      <ReviewSkillsStandardsModal />
      <AISuggestionsModalV2
        opened={aiSuggestionsModalOpened}
        onClose={() => setAiSuggestionsModalOpened(false)}
        projectId={contextId}
      />
      <Paper radius='md' withBorder shadow='xs' p={isMobile ? 'md' : 'xl'} mx={isMobile ? rem(-20) : 0}>
        <Group position='apart' align='flex-start' mb='md'>
          <div>
            <Title order={2}>
              {standardsOnly ? t('new_project_page.title_standards') : t('new_project_page.title_standards_and_skills')}
            </Title>
            <Text color='dimmed'>
              {standardsOnly
                ? t('new_project_page.description_standards')
                : t('new_project_page.description_standards_and_skills')}
            </Text>
          </div>
          {askAIEnabled && (!projectId || window.location.pathname.includes('/projects/new')) && (
            <Button
              leftIcon={<IconSparkles size={18} />}
              variant='light'
              color='grape'
              size='sm'
              onClick={() => {
                setAiSuggestionsModalOpened(true)
              }}
            >
              {t('new_project_page.ai_suggestions')}
            </Button>
          )}
        </Group>
        <Divider my='md' variant='solid' ref={targetRef} />
        <TextSearch
          onChange={handleChangeSearch}
          placeholder={
            skillsStandards.byCycle
              ? t('new_project_page.search_standards_and_skills_cycle')
              : t('new_project_page.search_standards_and_skills')
          }
        />
        {searchTerm && searchResults ? (
          <ListSearchResults />
        ) : (
          <Tabs
            keepMounted
            defaultValue={standardsOnly ? 'content' : 'skill'}
            value={currentTab}
            onTabChange={value => handleChangeTab(value)}
            mb='md'
          >
            <Tabs.List>
              {standardsOnly ? null : (
                <Tabs.Tab value='skill' icon={<IconMoodCog size='1.5rem' />}>
                  {t('new_project_page.skills_tab')}{' '}
                  {selectedSkills.length > 0 ? (
                    <Text color='dimmed' span={!isMobile} fw={400} size='sm'>
                      {!isMobile ? ' - ' : null}
                      {selectedSkills.length} {t('new_project_page.skills_selected')}
                    </Text>
                  ) : null}
                </Tabs.Tab>
              )}
              <Tabs.Tab value='content' icon={<IconBooks size='1.5rem' />}>
                {t('new_project_page.standards_tab')}{' '}
                {selectedStandards.length > 0 ? (
                  <Text color='dimmed' span={!isMobile} fw={400} size='sm'>
                    {!isMobile ? ' - ' : null}
                    {selectedStandards.length} {t('new_project_page.standards_selected')}
                  </Text>
                ) : null}
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value='skill' pt='md'>
              {page === 2 ? <ListSkillsStandardsPage2 /> : <ListSkillsStandardsPage1 />}
            </Tabs.Panel>
            <Tabs.Panel value='content' pt='md'>
              {page === 2 ? <ListSkillsStandardsPage2 /> : <ListSkillsStandardsPage1 />}
            </Tabs.Panel>
          </Tabs>
        )}
        <ProjectStepsButtons hasChanges={checkMutations} isSkillsStandardsStep={true} />
      </Paper>
    </>
  )
}
